// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CampaignChatList.test.tsx
import { type GetAskPunttProjectResponse } from "@mg/schemas/src/christo/askPunttProject";
import { CircleNotch, Lock, Plus, Users } from "@phosphor-icons/react";

import { PunttAvatar } from "./PunttAvatar";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";

type ProjectChat = GetAskPunttProjectResponse["chats"][number];
type ProjectMember = NonNullable<
  GetAskPunttProjectResponse["project"]["members"]
>[number];

function formatChatTime(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (sameDay)
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  if (isYesterday) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * Master list for the project's "My chats" tab: the requester's personal chats
 * (and any they've shared into the project), rendered as selectable rows with
 * the owner avatar, a Puntt avatar, the title, a Private/Shared badge, and the
 * last-updated time. Selecting a row opens the chat detail view. Matches the
 * Slack-style "channel list → conversation" master-detail of the chat surface.
 */
export function CampaignChatList({
  chats,
  currentUserId,
  members,
  onSelect,
  onNewChat,
  isCreating = false,
}: {
  chats: ProjectChat[];
  currentUserId: string | undefined;
  members: ProjectMember[];
  onSelect: (chatId: string) => void;
  onNewChat: () => void;
  isCreating?: boolean;
}) {
  const memberById = new Map(members.map((m) => [m._id, m]));

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5"
      data-testid="campaign-chat-list"
      aria-label="My chats"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-puntt-neutral-gray-12">
            My chats
          </h2>
          <p className="mt-0.5 text-[13px] text-puntt-neutral-gray-10">
            Private to you — share any chat to bring it into the project feed.
          </p>
        </div>
        <button
          type="button"
          onClick={onNewChat}
          disabled={isCreating}
          aria-label="New chat"
          data-testid="campaign-new-personal-chat"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-puntt-neutral-gray-7 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2 hover:text-puntt-accent-9 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isCreating ? (
            <CircleNotch size={14} weight="bold" className="animate-spin" />
          ) : (
            <Plus size={14} weight="bold" />
          )}
          New chat
        </button>
      </div>

      {chats.length === 0 ? (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-1 text-center"
          data-testid="campaign-chat-list-empty"
        >
          <p className="text-[14px] font-medium text-puntt-neutral-gray-12">
            No chats yet
          </p>
          <p className="text-[13px] text-puntt-neutral-gray-10">
            Start a private chat with Puntt, then share it to the project when
            you&apos;re ready.
          </p>
        </div>
      ) : (
        <ul
          className="flex flex-col gap-2"
          data-testid="campaign-chat-list-rows"
        >
          {chats.map((chat) => {
            const isOwn = !currentUserId || chat.userId === currentUserId;
            const owner = memberById.get(chat.userId);
            const ownerName = isOwn
              ? "You"
              : (owner?.name ?? owner?.email ?? "Member");
            const isShared = chat.projectChatKind === "shared";
            return (
              <li key={chat._id}>
                <button
                  type="button"
                  onClick={() => onSelect(chat._id)}
                  data-testid={`campaign-chat-card-${chat._id}`}
                  aria-label={`Open chat ${chat.title}`}
                  className="flex w-full items-center gap-3 rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-3 py-2.5 text-left transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2"
                >
                  <span className="flex shrink-0 items-center -space-x-2">
                    <AvatarWithInitials
                      size={7}
                      name={ownerName}
                      avatar={owner?.avatar ?? undefined}
                      className="ring-2 ring-base-white"
                    />
                    <PunttAvatar size={7} className="ring-2 ring-base-white" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-[13px] font-semibold text-puntt-neutral-gray-12">
                        {chat.title}
                      </span>
                      <span
                        className="flex shrink-0 items-center gap-1 rounded-full bg-puntt-neutral-gray-3 px-1.5 py-0.5 text-[10px] font-medium text-puntt-neutral-gray-11"
                        data-testid={`campaign-chat-badge-${chat._id}`}
                      >
                        {isShared ? (
                          <Users size={10} aria-hidden="true" />
                        ) : (
                          <Lock size={10} aria-hidden="true" />
                        )}
                        {isShared ? "Shared" : "Private"}
                      </span>
                    </span>
                    <span className="truncate text-[12px] text-puntt-neutral-gray-10">
                      {ownerName}
                    </span>
                  </span>
                  <span className="shrink-0 text-[11px] text-puntt-neutral-gray-9">
                    {formatChatTime(chat.updatedAt as string | undefined)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
